<template>
    <v-dialog v-model="visible" width="800px" transition="dialog-top-transition">
        <v-card :theme="isDarkMode ? 'dark' : 'light'">
            <v-toolbar density="compact">
                <v-card-title>
                    Spielervergleich
                </v-card-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <div v-if="$store.state.playersToCompare.length < 2" class="text-center pa-4">
                <v-icon size="x-large" class="mb-3">mdi-compare</v-icon>
                <p>Bitte wählen Sie mindestens zwei Spieler zum Vergleich aus.</p>
            </div>

            <div v-else class="py-4">
                <div class="d-flex flex-wrap gap-1 px-3 mb-2">
                    <v-chip v-for="(player, index) in $store.state.playersToCompare" :key="player.display_name"
                        :color="getPlayerColor(index)" closable @click:close="removePlayer(player.display_name)"
                        class="mr-2 mb-2 aldrich-font" variant="outlined">
                        {{ player.display_name }} ({{ player.Mannschaft }})
                    </v-chip>
                </div>

                <!-- Tabs für verschiedene Statistiken -->
                <v-tabs v-model="activeTab" class="mb-4">
                    <v-tab value="radar"> <v-icon size="large">mdi-chart-line</v-icon>
                    </v-tab>
                    <v-tab value="stats"> <v-icon size="large">mdi-table-large</v-icon>
                    </v-tab>
                </v-tabs>

                <v-window v-model="activeTab" class="border-t-lg" :touch="false">
                    <!-- Radar Chart -->
                    <v-window-item value="radar">
                        <div ref="radarChartContainer" class="chart-container">
                            <v-chart ref="radarChart" :option="radarChartOption"
                                :style="`height:${chart_height}px; width:${chart_width}px; min-height: 400px;`"
                                :theme="isDarkMode ? 'dark' : 'light'" autoresize></v-chart>
                        </div>
                    </v-window-item>

                    <!-- Statistiken Tabelle - Erweitert -->
                    <v-window-item value="stats">
                        <div class="table-container">
                            <v-table>
                                <thead class="border-top-0">
                                    <tr>
                                        <th class="text-left sticky-column bg-surface">Spieler</th>
                                        <th class="text-center">Spiele</th>
                                        <th class="text-center">Tore</th>
                                        <th class="text-center">Tore/Spiel</th>
                                        <th class="text-center">Feldtore</th>
                                        <th class="text-center">Feldtore/Spiel</th>
                                        <th class="text-center">7m Tore</th>
                                        <th class="text-center">7m Geworfen</th>
                                        <th class="text-center">7m Quote</th>
                                        <th class="text-center">Verwarnungen</th>
                                        <th class="text-center">2 Min.</th>
                                        <th class="text-center">Disqualif.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(player, index) in $store.state.playersToCompare"
                                        :key="player.display_name">
                                        <td class="font-weight-bold sticky-column bg-surface">
                                            <span :style="`color: ${getPlayerColor(index)}; font-weight: bold;`">
                                                {{ player.display_name }}
                                            </span>
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Spiele', player.Spiele) }">
                                            {{ player.Spiele }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Tore', player.Tore) }">
                                            {{ player.Tore }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Durchschnitt', player.Durchschnitt) }">
                                            {{ formatDecimal(player.Durchschnitt) }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Feldtore', player.Feldtore) }">
                                            {{ player.Feldtore }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Feldtore_pro_Spiel', player.Feldtore_pro_Spiel) }">
                                            {{ formatDecimal(player.Feldtore_pro_Spiel) }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Siebenmeter_Tore', player.Siebenmeter_Tore) }">
                                            {{ player.Siebenmeter_Tore }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Siebenmeter_geworfen', player.Siebenmeter_geworfen) }">
                                            {{ player.Siebenmeter_geworfen }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isSevenMeterPercentageTop(player) }">
                                            {{ calculateSevenMeterPercentage(player) }}%
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Verwarnungen', player.Verwarnungen || 0, true) }">
                                            {{ player.Verwarnungen || 0 }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Zeitstrafen', player.Zeitstrafen, true) }">
                                            {{ player.Zeitstrafen }}
                                        </td>
                                        <td class="text-center"
                                            :class="{ 'bg-green-lighten-4': isTopValue('Disqualifikationen', player.Disqualifikationen || 0, true) }">
                                            {{ player.Disqualifikationen || 0 }}
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </div>
                    </v-window-item>
                </v-window>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import ECharts from "vue-echarts";
import "echarts/theme/dark";

export default {
    name: 'PlayerCompareModal',
    components: {
        "v-chart": ECharts,
    },
    data() {
        return {
            visible: false,
            activeTab: 'radar',
            colorPool: [
                '#1976D2', // blue
                '#388E3C', // green
                '#D32F2F', // red
                '#FFA000', // amber
                '#7B1FA2', // purple
                '#0097A7', // cyan
                '#C2185B', // pink
                '#F57C00', // orange
                '#512DA8', // deep-purple
                '#00796B', // teal
                '#303F9F', // indigo
                '#5D4037', // brown
                '#616161', // grey
                '#263238', // blue-grey
                '#006064', // cyan-darken-4
                '#827717', // lime-darken-4
                '#E65100', // orange-darken-4
                '#3E2723', // brown-darken-4
                '#880E4F', // pink-darken-4
                '#1A237E', // indigo-darken-4
            ],
            usedColors: {},
            chart_width: 0,
            chart_height: 400
        };
    },
    computed: {
        isDarkMode() {
            return this.$store.state.isDarkMode;
        },
        isMobile() {
            return this.$vuetify.display.smAndDown;
        },
        radarChartOption() {
            const players = this.$store.state.playersToCompare;
            if (players.length < 2) return { series: [] };

            const maxValues = this.calculateMaxValues(players);
            return {
                tooltip: {
                    trigger: 'item'
                },
                legend: {
                    orient: 'horizontal',
                    left: 10,
                    top: 10,
                    itemGap: 20,
                },
                textStyle: {
                    fontFamily: 'Aldrich',
                    fontSize: this.isMobile ? 10 : 13
                },
                radar: {
                    indicator: [
                        { name: 'Tore/Spiel', max: maxValues.durchschnitt * 1.2 },
                        { name: 'Feldtore/Spiel', max: maxValues.feldtoreProSpiel * 1.2 },
                        { name: '7m Quote', max: 100 },
                        { name: 'Tore', max: maxValues.tore * 1.2 },
                        { name: 'Feldtore', max: maxValues.feldtore * 1.2 },
                        { name: '7m Tore', max: maxValues.siebenmeterTore * 1.2 },
                        { name: 'Verwarnungen', max: maxValues.verwarnungen * 1.2 },
                        { name: '2 Min.', max: maxValues.zeitstrafen * 1.2 },
                        { name: 'Disqualif.', max: maxValues.disqualifikationen * 1.2 },
                        { name: 'Spiele', max: maxValues.spiele * 1.2 }
                    ],
                    radius: this.isMobile ? '50%' : '70%',
                    center: ['50%', '57%']
                },
                series: [{
                    type: 'radar',
                    data: players.map((player, index) => ({
                        name: player.display_name,
                        value: [
                            this.formatDecimal(player.Durchschnitt),
                            this.formatDecimal(player.Feldtore_pro_Spiel),
                            this.calculateSevenMeterPercentage(player),
                            player.Tore,
                            player.Feldtore,
                            player.Siebenmeter_Tore,
                            player.Verwarnungen || 0,
                            player.Zeitstrafen,
                            player.Disqualifikationen || 0,
                            player.Spiele
                        ],
                        itemStyle: {
                            color: this.getPlayerColor(index)
                        },
                        areaStyle: { opacity: 0.3 },
                        lineStyle: { width: 2 }
                    }))
                }],
                backgroundColor: 'transparent'
            };
        },
    },
    methods: {
        close() {
            this.visible = false;
            document.body.removeChild(this.$el.parentNode);
        },
        removePlayer(playerName) {
            this.$store.commit('removePlayerFromCompare', playerName);
        },
        calculateSevenMeterPercentage(player) {
            if (player.Siebenmeter_geworfen === 0) {
                return "0.00";
            }

            const percentage = (player.Siebenmeter_Tore / player.Siebenmeter_geworfen) * 100;
            const formatted = this.formatDecimal(percentage);
            return formatted;
        },
        formatDecimal(value) {
            return parseFloat(value).toFixed(2);
        },
        getPlayerColor(index) {
            const playerName = this.$store.state.playersToCompare[index].display_name;

            // Check if this player already has an assigned color
            if (this.usedColors[playerName]) {
                return this.usedColors[playerName];
            }

            // Assign a new color from the pool
            const colorIndex = index % this.colorPool.length;
            const color = this.colorPool[colorIndex];
            this.usedColors[playerName] = color;

            return color;
        },
        // Spezielle Methode für die 7m-Quote Markierung
        isSevenMeterPercentageTop(player) {
            const players = this.$store.state.playersToCompare;
            if (players.length < 2) return false;

            // Berechne die 7m-Quote für alle Spieler als Rohwerte (nicht formatierte Strings)
            const percentages = players.map(p => {
                if (p.Siebenmeter_geworfen === 0) return 0;
                const pct = (p.Siebenmeter_Tore / p.Siebenmeter_geworfen) * 100;
                return pct;
            });

            // Finde den höchsten Wert
            const maxPercentage = Math.max(...percentages);

            // Berechne den Wert für den aktuellen Spieler
            const playerPercentage = player.Siebenmeter_geworfen === 0 ?
                0 : (player.Siebenmeter_Tore / player.Siebenmeter_geworfen) * 100;


            // Vergleich mit einer kleinen Toleranz
            const isMax = Math.abs(playerPercentage - maxPercentage) < 0.001;

            return isMax;
        },
        isTopValue(category, value, isNegative = false) {
            const players = this.$store.state.playersToCompare;
            if (players.length < 2) return false;

            // Determine if this is the best value in this category
            if (isNegative) {
                // For negative stats (like penalties), lower is better
                const minValue = Math.min(...players.map(p => {
                    return parseFloat(p[category] || 0);
                }));

                return Math.abs(parseFloat(value) - minValue) < 0.001;
            } else {
                // For positive stats, higher is better
                const maxValue = Math.max(...players.map(p => {
                    return parseFloat(p[category] || 0);
                }));
                // Toleranz für Fließkomma-Vergleich
                return Math.abs(parseFloat(value) - maxValue) < 0.001;
            }
        },
        calculateMaxValues(players) {
            return {
                durchschnitt: Math.max(...players.map(p => p.Durchschnitt)),
                feldtoreProSpiel: Math.max(...players.map(p => p.Feldtore_pro_Spiel)),
                zeitstrafen: Math.max(...players.map(p => p.Zeitstrafen)),
                tore: Math.max(...players.map(p => p.Tore)),
                feldtore: Math.max(...players.map(p => p.Feldtore)),
                siebenmeterTore: Math.max(...players.map(p => p.Siebenmeter_Tore)),
                verwarnungen: Math.max(...players.map(p => p.Verwarnungen || 0)),
                disqualifikationen: Math.max(...players.map(p => p.Disqualifikationen || 0)),
                spiele: Math.max(...players.map(p => p.Spiele))
            };
        },
        updateChartDimensions() {
            this.$nextTick(() => {
                let container = this.$refs.radarChartContainer;
                if (container) {
                    const { width, height } = container.getBoundingClientRect();
                    this.chart_width = width;
                    this.chart_height = height || 400;
                }
            });
        },
        forceChartResize() {
            this.$nextTick(() => {
                if (this.activeTab === 'radar' && this.$refs.radarChart) {
                    this.$refs.radarChart.resize();
                }
            });
        }
    },
    mounted() {
        this.visible = true;
        this.$nextTick(() => {
            this.updateChartDimensions();
            this.forceChartResize();
        });
    },
    watch: {
        activeTab() {
            this.$nextTick(() => {
                this.updateChartDimensions();
                this.forceChartResize();
            });
        },
        visible(newValue) {
            if (newValue) {
                this.$nextTick(() => {
                    this.updateChartDimensions();
                    this.forceChartResize();
                });
            }
        }
    }
};
</script>

<style>
.v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chart-container {
    width: 100%;
    min-height: 400px;
}

/* Add horizontal scrolling for the stats table on smaller screens */
.table-container {
    width: 100%;
    overflow-x: auto;
}

.sticky-column {
    position: sticky;
    left: 0;
    z-index: 1;
    /* Using bg-surface Vuetify class for background */
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

/* Apply a custom style for the top values if needed */
.v-table :deep(.bg-green-lighten-4) {
    opacity: 0.8;
    /* Makes the green more consistent across themes */
}
</style>